import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { PersistOptions } from 'zustand/middleware';
import type { ShopList } from '@/types/shop';
import { getShopList } from '@/api/shop';
import { SESSION_NAME } from '@/config/routes';

// 세션 스토리지에 저장할 상태의 타입 정의
type ShopPersist = {
  shopList: ShopList[];
  selectedShop: ShopList | null;
};

interface ShopState {
  shopList: ShopList[];
  selectedShop: ShopList | null;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  fetchShopList: () => Promise<void>;
  setSelectedShop: (shop: ShopList) => void;
  clearShops: () => void;
}

// 세션 스토리지 사용을 위한 persist 설정
const persistConfig: PersistOptions<ShopState, ShopPersist> = {
  name: `${SESSION_NAME}-shop`,
  storage: {
    getItem: (name) => {
      const value = sessionStorage.getItem(name);
      return value ? JSON.parse(value) : null;
    },
    setItem: (name, value) => {
      sessionStorage.setItem(name, JSON.stringify(value));
    },
    removeItem: (name) => {
      sessionStorage.removeItem(name);
    }
  },
  partialize: (state) => ({
    shopList: state.shopList,
    selectedShop: state.selectedShop,
  })
};

export const useShopStore = create<ShopState>()(
  persist(
    (set, get) => ({
      shopList: [],
      selectedShop: null,
      isLoading: false,
      error: null,

      fetchShopList: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await getShopList();
          
          if (response.code === 200 && response.data) {
            const shopList = response.data;
            
            // shopNo가 1인 데이터를 기본 선택 샵으로 설정
            const defaultShop = shopList.find(shop => shop.shopNo === 1) || shopList[0] || null;
            
            set({ 
              shopList,
              selectedShop: defaultShop,
              isLoading: false,
              error: null 
            });
          } else {
            set({ 
              isLoading: false, 
              error: response.message || '샵 목록을 가져오는데 실패했습니다.' 
            });
          }
        } catch (error) {
          console.error('샵 목록 조회 오류:', error);
          let errorMessage = '샵 목록을 가져오는 중 오류가 발생했습니다.';
          if (error instanceof Error) {
            errorMessage = error.message || errorMessage;
          }
          set({ isLoading: false, error: errorMessage });
        }
      },

      setSelectedShop: (shop: ShopList) => {
        console.log('선택된 샵 변경:', shop);
        set({ selectedShop: shop });
        
        // 샵 변경 이벤트 발생 (다른 컴포넌트에서 리스닝할 수 있도록)
        window.dispatchEvent(new CustomEvent('shop-changed', { detail: shop }));
      },

      clearShops: () => {
        set({ 
          shopList: [], 
          selectedShop: null, 
          isLoading: false, 
          error: null 
        });
        
        // 세션 스토리지에서도 제거
        sessionStorage.removeItem(`${SESSION_NAME}-shop`);
      },
    }),
    persistConfig
  )
);